import { useState, useEffect, useRef, useCallback } from "react";
import {
  adminGetAppVersion, adminUploadApk,
  adminListApkVersions, adminActivateApkVersion, adminDeleteApkVersion, adminApkVersionDownloadUrl
} from "@/api";
import { toast } from "sonner";
import {
  Smartphone, Upload, FileArchive, Loader2, CheckCircle2, AlertTriangle,
  ExternalLink, RefreshCcw, Hash, Calendar, User, FileCheck, X, History,
  RotateCcw, Trash2, Download
} from "lucide-react";

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit"
    });
  } catch { return iso; }
};

export default function AdminAppRelease() {
  const [versionInfo, setVersionInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  // ---- Version history state ----
  const [versions, setVersions] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [busyVersionId, setBusyVersionId] = useState(null); // for activate/delete spinners
  const [confirm, setConfirm] = useState(null); // {type:'activate'|'delete', version}

  const [file, setFile] = useState(null);
  const [versionName, setVersionName] = useState("");
  const [versionCode, setVersionCode] = useState("");
  const [dragActive, setDragActive] = useState(false);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);

  const fileInputRef = useRef(null);

  const loadVersion = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminGetAppVersion();
      setVersionInfo(res.data);
    } catch (err) {
      toast.error("Could not fetch current version");
    } finally {
      setLoading(false);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await adminListApkVersions();
      setVersions(res.data?.versions || []);
    } catch (err) {
      console.error(err);
      toast.error("Could not fetch version history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const reloadAll = useCallback(async () => {
    await Promise.all([loadVersion(), loadHistory()]);
  }, [loadVersion, loadHistory]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // ---- File picker / drag-and-drop ----
  const validateAndSetFile = (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".apk")) {
      toast.error("File must have a .apk extension");
      return;
    }
    if (f.size < 100 * 1024) {
      toast.error("APK looks too small (< 100 KB) — likely corrupt");
      return;
    }
    if (f.size > 200 * 1024 * 1024) {
      toast.error("APK is too large (> 200 MB)");
      return;
    }
    setFile(f);
    setUploadResult(null);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
    else if (e.type === "dragleave") setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const f = e.dataTransfer?.files?.[0];
    validateAndSetFile(f);
  };

  const handleFileInput = (e) => validateAndSetFile(e.target.files?.[0]);

  // ---- Upload ----
  const handleUpload = async () => {
    if (!file) { toast.error("Pick an APK file first"); return; }
    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    try {
      const res = await adminUploadApk(file, versionName.trim(), versionCode ? parseInt(versionCode) : 0, (e) => {
        if (e.total) setUploadProgress(Math.round((e.loaded / e.total) * 100));
      });
      setUploadResult(res.data);
      toast.success(`APK published — ${res.data.size_mb} MB`);
      setFile(null);
      setVersionName("");
      setVersionCode("");
      if (fileInputRef.current) fileInputRef.current.value = "";
      await reloadAll();
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message;
      toast.error(`Upload failed: ${detail}`);
    } finally {
      setUploading(false);
    }
  };

  // ---- Activate / rollback ----
  const handleActivate = async (v) => {
    setBusyVersionId(v.id);
    try {
      await adminActivateApkVersion(v.id);
      toast.success(`Rolled back to v${v.version_name || v.version_code} — now active`);
      setConfirm(null);
      await reloadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Activation failed");
    } finally {
      setBusyVersionId(null);
    }
  };

  // ---- Delete ----
  const handleDelete = async (v) => {
    setBusyVersionId(v.id);
    try {
      await adminDeleteApkVersion(v.id);
      toast.success(`Deleted v${v.version_name || v.version_code}`);
      setConfirm(null);
      await reloadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyVersionId(null);
    }
  };

  // ---- Render ----
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Smartphone className="h-7 w-7 text-blue-600" />
            App Release
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Publish a new Rebuilt Android APK to{" "}
            <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">/download</code>
          </p>
        </div>
        <button
          onClick={reloadAll}
          data-testid="refresh-version"
          className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"
        >
          <RefreshCcw className="h-4 w-4" />
          Refresh
        </button>
      </div>

      {/* Current version card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">Currently published</h2>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading…
          </div>
        ) : versionInfo?.available ? (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div>
              <div className="text-xs text-slate-500 mb-1">Version</div>
              <div className="font-bold text-slate-900">{versionInfo.version_name || "—"}</div>
              <div className="text-xs text-slate-400">build {versionInfo.version_code || "—"}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1">Size</div>
              <div className="font-bold text-slate-900">{versionInfo.size_mb} MB</div>
              <div className="text-xs text-slate-400">{formatBytes(versionInfo.size_bytes)}</div>
            </div>
            <div>
              <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Calendar className="h-3 w-3" /> Uploaded</div>
              <div className="font-medium text-slate-900 text-sm">{formatDate(versionInfo.uploaded_at)}</div>
            </div>
            <div className="flex items-end">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/download/apk`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold"
              >
                <ExternalLink className="h-3.5 w-3.5" />
                Test download
              </a>
            </div>
            {versionInfo.sha256 && (
              <div className="col-span-2 sm:col-span-4 pt-3 border-t border-slate-100">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Hash className="h-3 w-3" /> SHA-256</div>
                <code className="text-[11px] font-mono text-slate-600 break-all">{versionInfo.sha256}</code>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-6">
            <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
            <p className="text-sm text-slate-700 font-medium">No APK published yet</p>
            <p className="text-xs text-slate-500 mt-1">Upload one below to make it available on /download</p>
          </div>
        )}
      </div>

      {/* Upload card */}
      <div className="rounded-2xl border border-slate-200 bg-white p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-1">Publish new APK</h2>
        <p className="text-sm text-slate-500 mb-5">
          The file is validated (.apk extension, ZIP magic bytes, 100 KB – 200 MB) and saved as{" "}
          <code className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">rebuilt-app.apk</code>{" "}
          on the backend. The previous APK is overwritten — there is no version history yet.
        </p>

        {/* Drag-drop zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
            dragActive
              ? "border-blue-500 bg-blue-50"
              : file
              ? "border-green-300 bg-green-50/50"
              : "border-slate-300 bg-slate-50 hover:border-blue-400"
          }`}
          data-testid="apk-drop-zone"
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".apk,application/vnd.android.package-archive"
            onChange={handleFileInput}
            className="hidden"
            data-testid="apk-file-input"
          />

          {file ? (
            <div className="flex items-center justify-center gap-3">
              <div className="h-12 w-12 rounded-xl bg-green-100 flex items-center justify-center">
                <FileCheck className="h-6 w-6 text-green-600" />
              </div>
              <div className="text-left">
                <div className="font-semibold text-slate-900">{file.name}</div>
                <div className="text-xs text-slate-500">{formatBytes(file.size)} · ready to upload</div>
              </div>
              <button
                onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                className="ml-3 p-1.5 rounded-lg hover:bg-slate-200 text-slate-500"
                data-testid="apk-clear"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <FileArchive className="h-10 w-10 text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700 mb-1">
                Drag and drop the signed <code className="text-blue-600">rebuilt-app.apk</code> here
              </p>
              <p className="text-xs text-slate-500 mb-4">or</p>
              <button
                onClick={() => fileInputRef.current?.click()}
                data-testid="apk-pick-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700"
              >
                <Upload className="h-4 w-4" />
                Choose APK file
              </button>
            </>
          )}
        </div>

        {/* Version metadata inputs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
              Version name <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={versionName}
              onChange={(e) => setVersionName(e.target.value)}
              placeholder="e.g. 1.4.5"
              data-testid="version-name-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">Read from build.gradle if left blank</p>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 mb-1 block">
              Version code <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="number"
              value={versionCode}
              onChange={(e) => setVersionCode(e.target.value)}
              placeholder="e.g. 145"
              data-testid="version-code-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
            />
            <p className="text-[11px] text-slate-400 mt-1">Used for "newer than installed?" comparisons</p>
          </div>
        </div>

        {/* Upload progress */}
        {uploading && (
          <div className="mt-5">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1.5">
              <span>Uploading {file?.name}…</span>
              <span className="font-semibold">{uploadProgress}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div
                className="h-full bg-blue-600 transition-all"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
          </div>
        )}

        {/* Upload button */}
        <button
          onClick={handleUpload}
          disabled={!file || uploading}
          data-testid="apk-upload-btn"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ background: "linear-gradient(135deg, #1565C0 0%, #00BCD4 100%)" }}
        >
          {uploading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Upload className="h-5 w-5" />
              Publish APK
            </>
          )}
        </button>

        {/* Success result */}
        {uploadResult && (
          <div className="mt-5 p-4 rounded-xl bg-green-50 border border-green-200" data-testid="upload-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold text-green-900 mb-1">APK published successfully</div>
                <div className="text-green-800 text-xs space-y-0.5">
                  <div>Size: <span className="font-semibold">{uploadResult.size_mb} MB</span></div>
                  {uploadResult.version_name && (
                    <div>Version: <span className="font-semibold">{uploadResult.version_name}</span> (build {uploadResult.version_code})</div>
                  )}
                  <div className="break-all">SHA-256: <code className="text-[10px]">{uploadResult.sha256}</code></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Version History */}
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-slate-600" />
            <h2 className="text-lg font-bold text-slate-900">Version history</h2>
            <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full" data-testid="versions-count">
              {versions.length}
            </span>
          </div>
          <p className="text-xs text-slate-500 hidden sm:block">
            Click <strong>Rollback</strong> to make any previous version live.
          </p>
        </div>

        {historyLoading ? (
          <div className="flex items-center justify-center py-10 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading history…
          </div>
        ) : versions.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            No APK versions uploaded yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="versions-table">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Version</th>
                  <th className="text-left px-4 py-3 font-semibold">Size</th>
                  <th className="text-left px-4 py-3 font-semibold">Uploaded</th>
                  <th className="text-left px-4 py-3 font-semibold">By</th>
                  <th className="text-left px-4 py-3 font-semibold">SHA-256</th>
                  <th className="text-right px-4 py-3 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {versions.map((v) => (
                  <tr
                    key={v.id}
                    className={`border-t border-slate-100 ${v.is_active ? "bg-blue-50/40" : "hover:bg-slate-50"}`}
                    data-testid={`version-row-${v.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div>
                          <div className="font-semibold text-slate-900">{v.version_name || "—"}</div>
                          <div className="text-[11px] text-slate-500">build {v.version_code || "—"}</div>
                        </div>
                        {v.is_active && (
                          <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-green-100 text-green-700 uppercase tracking-wide" data-testid={`active-badge-${v.id}`}>
                            <CheckCircle2 className="h-3 w-3" /> Active
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{v.size_mb} MB</td>
                    <td className="px-4 py-3 text-slate-700">{formatDate(v.uploaded_at)}</td>
                    <td className="px-4 py-3 text-slate-700 text-xs">{v.uploaded_by || "—"}</td>
                    <td className="px-4 py-3">
                      <code className="text-[10px] font-mono text-slate-500 break-all">
                        {(v.sha256 || "").slice(0, 16)}…
                      </code>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <a
                          href={adminApkVersionDownloadUrl(v.id)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                          title="Download this version"
                          data-testid={`download-version-${v.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </a>
                        {!v.is_active && (
                          <button
                            onClick={() => setConfirm({ type: "activate", version: v })}
                            disabled={busyVersionId === v.id}
                            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-blue-700 bg-blue-50 hover:bg-blue-100 disabled:opacity-50"
                            title="Make this version active (rollback)"
                            data-testid={`rollback-${v.id}`}
                          >
                            {busyVersionId === v.id ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <RotateCcw className="h-3.5 w-3.5" />
                            )}
                            Rollback
                          </button>
                        )}
                        <button
                          onClick={() => setConfirm({ type: "delete", version: v })}
                          disabled={v.is_active || busyVersionId === v.id}
                          className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={v.is_active ? "Cannot delete the active version" : "Delete this version"}
                          data-testid={`delete-version-${v.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirm modal (activate / delete) */}
      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setConfirm(null)}>
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
            data-testid="confirm-modal"
          >
            <div className="flex items-start gap-3 mb-4">
              <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                confirm.type === "delete" ? "bg-rose-100 text-rose-600" : "bg-blue-100 text-blue-600"
              }`}>
                {confirm.type === "delete" ? <Trash2 className="h-5 w-5" /> : <RotateCcw className="h-5 w-5" />}
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-slate-900">
                  {confirm.type === "delete" ? "Delete version?" : "Rollback to this version?"}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {confirm.type === "delete" ? (
                    <>
                      This will permanently delete <strong>v{confirm.version.version_name || confirm.version.version_code}</strong>{" "}
                      from the server. The file cannot be recovered.
                    </>
                  ) : (
                    <>
                      <strong>v{confirm.version.version_name || confirm.version.version_code}</strong> will become the
                      active version served at <code className="px-1 bg-slate-100 rounded">/download</code>. Customers
                      installing the APK from this point will receive this version.
                    </>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirm(null)}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-100"
                data-testid="confirm-cancel"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  confirm.type === "delete"
                    ? handleDelete(confirm.version)
                    : handleActivate(confirm.version)
                }
                disabled={busyVersionId === confirm.version.id}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white disabled:opacity-50 ${
                  confirm.type === "delete"
                    ? "bg-rose-600 hover:bg-rose-700"
                    : "bg-blue-600 hover:bg-blue-700"
                }`}
                data-testid="confirm-action"
              >
                {busyVersionId === confirm.version.id && <Loader2 className="h-4 w-4 animate-spin" />}
                {confirm.type === "delete" ? "Delete" : "Rollback"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Help / tips */}
      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-sm text-slate-600">
        <h3 className="font-semibold text-slate-800 mb-2 flex items-center gap-1.5">
          <User className="h-4 w-4" /> Tips
        </h3>
        <ul className="space-y-1.5 text-xs list-disc pl-5">
          <li>Use the <strong>signed release APK</strong> only — the unsigned debug APK will not install on user phones.</li>
          <li>Each upload is <strong>added to the version history</strong> below — old versions are NOT overwritten. The newest upload automatically becomes active. Use <strong>Rollback</strong> to switch the active version to any older one.</li>
          <li>If you have <code>APK_UPLOAD_TOKEN</code> set on the backend and matching <code>APK_UPLOAD_TOKEN</code> + <code>BACKEND_BASE_URL</code> as GitHub repo secrets, the GitHub Actions <code>build-android-apk.yml</code> workflow will auto-publish on every push to <code>main</code>. This page is the manual fallback / hotfix path.</li>
          <li>The active version cannot be deleted — first activate a different version (rollback), then delete the previous one.</li>
        </ul>
      </div>
    </div>
  );
}
